 import { html, LitElement, nothing } from "lit";
import { vesselOwnRouteReasonPopupStyles } from "../../vessel-own-route-reason-popup/src/vessel-own-route-reason-popup-styles";
import { customElement, property } from "lit/decorators";
import { ReasonOption } from "../../../src/models/schedule-change-model";

@customElement('vessel-clf-popup')
export class VesselCLFPopup extends LitElement {
    @property({ type: Boolean }) shown: boolean = false;
    @property({ type: Boolean }) hasDateInput: boolean = false;
    @property({ type: String }) headerText: string = '';
    @property({ type: String }) bodyText: string = '';
    @property({ type: String }) private _selectedReason?: string = ''
    @property({ type: String }) private _selectedReasonCode?: string = ''
    @property({ type: Object }) reasonOptions: Record<string, ReasonOption> = {};
    @property({ type: String }) etaBerth?: string = ''
    @property({ type: String }) etaPilot?: string = ''
    private _comment: any;
    private _date: any;
    private _dateInputValid: boolean = false;

    constructor(){
        super();
        this._renderModalContent = this._renderModalContent.bind(this);
        this._renderReasonOptions = this._renderReasonOptions.bind(this);
    }

    static get styles(){
        return vesselOwnRouteReasonPopupStyles
    }

    private _renderModalContent() {
        return html`
            <div class="desc">${this.bodyText}</div>
            ${this.etaBerth ? html`
                <div class="eta-header">
                    Earliest ETA <span>(in local time)</span>
                </div>
                <div class="eta">
                    <span class="eta-label">ETA Pilot:</span>
                    <span class="eta-value">${this.etaPilot}</span>,
                    <span class="eta-label">ETA Berth:</span>
                    <span class="eta-value">${this.etaBerth}</span>
                </div>
            ` : nothing}
            <div class="input-space">
                <mc-select
                    label="Reason"
                    @optionselected=${this._onReasonChange}
                    ?invalid=${!this._selectedReason}>
                    <span slot="errormessage">Please select a reason</span>
                    ${this._renderReasonOptions()}
                </mc-select>
            </div>
            ${this.hasDateInput ? html`
                <div class="inputAndHeader">
                    <span class="clf-date">Best ETA Pilot Station (in Local Time)</span>
                    <div class="inputContainer ${!this._dateInputValid ? "invalid" : ''}">
                        <input id="clf-date" type="datetime-local" min=${this._getMinDateTime()} @input=${this._onDateChange}/>
                    </div>
                    ${!this._dateInputValid ? html`
                        <span class="error-message">Please enter a valid date</span>
                    ` : nothing}
                </div>
            ` : nothing}
            <div class="comment-input">
                <mc-textarea
                    label=${'Comment'}
                    rows="4"
                    @input=${this._onCommentChange}
                >
                </mc-textarea>
            </div>
        `
    }
    
    render(){
    return html`
        <mc-modal
            heading=${this.headerText}
            ?open=${this.shown}
            height="auto"
            dimension="small"
            backdropcloseactiondisabled
            escapecloseactiondisabled
            entercloseactiondisabled
            hiddenclose
        >
            ${this._renderModalContent()}
            <div class="footer-clf">
            <mc-button
                    focusstartanchor
                    appearance="neutral"
                    variant="filled"
                    @click=${() => this.onCancel()}
                    >Back</mc-button
                >
                <mc-button
                    focusstartanchor
                    appearance="primary"
                    variant="filled"
                    @click=${() => this.onSubmit()}
                    ?disabled=${!this._isFormValid}
                    >Submit</mc-button
                >
            </div>
        </mc-modal> `  
    }

    private _renderReasonOptions() {
        return Object.entries(this.reasonOptions).map(([code, option]) => {
            const label = typeof option === "string" ? option : option.label;
            const definition = typeof option === "string" ? undefined : option.definition;

            return html`
            <mc-option value=${label} @focus=${(e: Event) => this._toggleSublabel(e, true)} @blur=${(e: Event) => this._toggleSublabel(e, false)} @click=${(e: Event) => this._toggleSublabel(e, false)}>
                ${label}
                ${definition ? html`<span slot="sublabel" hidden>${definition}</span>` : nothing}
            </mc-option>
            `;
        });
    }

    private _toggleSublabel(e: Event, show: boolean) {
        const option = e.currentTarget as HTMLElement;
        const sublabel = option.querySelector('[slot="sublabel"]') as HTMLElement | null;
        if (sublabel) {
            if (show) {
            sublabel.removeAttribute('hidden');
            } else {
            sublabel.setAttribute('hidden', '');
            }
        }
    }

    private _onCommentChange(e: Event) {
        const target = e.target as HTMLInputElement;
        this._comment = target.value;
        this.requestUpdate();
    }

    private _onReasonChange(e: Event) {
        const target = e.target as HTMLSelectElement
        this._selectedReason = target.value;
        this._selectedReasonCode = Object.keys(this.reasonOptions).find(key => {
            const option = this.reasonOptions[key];
            return typeof option === "string"
            ? option === this._selectedReason
            : option.label === this._selectedReason;
        });
        this.requestUpdate();
    }
    
    private _onDateChange(e: Event) {
        const input = e.target as HTMLInputElement;
        this._date = input.value;
        this._dateInputValid = !!this._date;
        this.requestUpdate();
    }
    
    private onSubmit(){
        this.dispatchEvent(new CustomEvent("submit", {
            detail: {
                reason: this._selectedReason,
                reasonCode: this._selectedReasonCode,
                comment: this._comment,
                date: this.hasDateInput ? this._date : undefined,
            },
            bubbles: true,
            composed: true,
        }));
    }

    private onCancel(){
        this.dispatchEvent(new CustomEvent("cancel", {
            bubbles: true,
            composed: true,
        }));
    }

    private get _isFormValid() {
        const hasReason = !!this._selectedReason;
        const hasDate = this.hasDateInput ? !!this._date : true;
        return hasReason && hasDate;
    }

    private _getMinDateTime(): string {
        const now = new Date();
        now.setSeconds(0, 0); // Remove seconds and milliseconds
        const tzOffset = -now.getTimezoneOffset(); // Convert to local time

        const pad = (n: number) => n.toString().padStart(2, '0');

        const yyyy = now.getFullYear();
        const mm = pad(now.getMonth() + 1);
        const dd = pad(now.getDate());
        const hh = pad(now.getHours());
        const min = pad(now.getMinutes());

        return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
    }
}